import moment from "moment";
import { useSnackbar } from "notistack";
import { Avatar, Box, Typography, Link, Grid } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AgGrid from "../../../components/Table/AgGrid";
import {
  dateToUnix,
  formatDate,
  unixToDateTimeWithFormat,
} from "../../../utils/date-converter";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedLetterAvatar from "../../../components/Custom/CustomizedLetterAvatar";
import {
  dateComparator,
  exportCSVParams,
  filterParamsOptions,
} from "../../../utils/filterparams";
import { entryTypeToThai, formatNumber } from "../../../utils/dataTransformer";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import TotalBox from "../../../components/UI/TotalBox";
import NewReportDateFilter from "../../../components/UI/NewReportDateFilter";
import { useForm } from "react-hook-form";
import { inventoryReportActions } from "../../../features/Inventory/Report/report-slice";
import { getAllTraceEntryReport } from "../../../features/Inventory/Report/report-actions";

import SettingService from "../../../services/Setting";

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
  cursor: "pointer",
}));

const TraceEntry = () => {
  const gridRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [sum, setSum] = useState();
  const { traceEntry, isLoading } = useSelector(
    (state) => state.inventoryReport
  );

  const navigateToGoodPage = (type, document_id) => {
    window.open(
      `/inventory/${type}/${encodeURIComponent(document_id)}`,
      "_blank"
    );
  };

  const breadcrumbs = [
    {
      name: t("inventory.index"),
      to: "/inventory",
    },
    {
      name: t("inventory.report"),
      to: "/inventory/report",
    },
    {
      name: t("inventory.traceEntry.index"),
    },
  ];

  const getAllItemGroupLevel1 = useCallback(async () => {
    const data = await SettingService.getAllCategory();
    return data;
  }, []);

  const columnDefs = [
    {
      field: "scanned_date",
      headerName: "วันที่สแกน",
      filter: "agDateColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("date"),
        comparator: dateComparator,
      },
    },
    {
      field: "created_date",
      headerName: t("inventory.stockEntry.createdDate"),
      filter: false,
    },
    {
      field: "reference_document_id",
      headerName: t("inventory.stockEntry.referenceDocument"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
      cellRenderer: (params) => {
        const { stock_entry, reference_document_id } = params.data;
        const { reference_document_type } = stock_entry;
        if (!reference_document_type || !reference_document_id) {
          return "-";
        }
        return (
          <StyledLink
            onClick={() =>
              navigateToGoodPage(reference_document_type, reference_document_id)
            }
          >
            {reference_document_id}
          </StyledLink>
        );
      },
    },
    {
      field: "item_item_group",
      headerName: t("inventory.items.itemGroup") + " " + 1,
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params) => {
          // fetch values from server
          const values = await getAllItemGroupLevel1();
          const formatValues = values.map((group) => group.name);
          params.success(formatValues);
        },
      },
    },
    {
      field: "item_document_id",
      headerName: t("inventory.items.itemId"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_name",
      headerName: t("inventory.items.itemName"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_description",
      headerName: t("inventory.items.itemDescription"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "posted_quantity",
      headerName: t("inventory.list.quantityDifference"),
      sortable: false,
      filter: false,
      valueFormatter: (params) => formatNumber(params.value),
    },
    {
      field: "uom_name",
      headerName: t("inventory.unit"),
      sortable: false,
      filter: false,
    },
    {
      field: "reference_document_type",
      headerName: t("inventory.stockEntry.entryDocumentType"),
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["นำเข้า", "นำออก", "โอนย้าย", "ปรับปรุง"],
      },
    },
    {
      field: "document_type",
      headerName: t("inventory.stockEntry.documentType"),
      filter: "agSetColumnFilter",
      filterParams: {
        values: [
          "ซื้อ",
          "ผลิต",
          "แปรรูป",
          "รับคืนจากลูกค้า",
          "รับคืนจากผลิต",
          "ขาย",
          "ขายออนไลน์",
          "ผลิตวัตถุดิบ",
          "ผลิตแปรรูป",
          "ผลิตแปรรูป - PT",
          "ส่งคืน",
          "สินค้าเสีย",
          "อื่นๆ",
        ],
      },
    },
    {
      field: "serial_number",
      headerName: t("inventory.serialNumber"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "source_warehouse_name",
      headerName: t("inventory.list.sourceWarehouse"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "source_bin_location_document_id",
      headerName: t("inventory.list.sourceBinLocation"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "destination_warehouse_name",
      headerName: t("inventory.list.destinationWarehouse"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "destination_bin_location_document_id",
      headerName: t("inventory.list.destinationBinLocation"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "lot",
      headerName: t("inventory.list.lotNumber"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "remark",
      headerName: t("inventory.remark"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "created_by_full_name",
      headerName: t("inventory.stockEntry.createdBy"),
      sortable: false,
      filter: "agSetColumnFilter",
      cellRenderer: (params) => {
        if (params.data.created_by.img_url) {
          return <Avatar alt="img_url" src={params.data.created_by.img_url} />;
        } else {
          return (
            <CustomizedLetterAvatar
              name={
                params.data.created_by.first_name +
                " " +
                params.data.created_by.last_name
              }
            />
          );
        }
      },
    },
    {
      field: "scanned_by_full_name",
      headerName: "ผู้สแกน",
      sortable: false,
      filter: "agSetColumnFilter",
      cellRenderer: (params) => {
        if (!params.data.scanned_by) return "";
        if (params.data.scanned_by.img_url) {
          return <Avatar alt="img_url" src={params.data.scanned_by.img_url} />;
        } else {
          return (
            <CustomizedLetterAvatar
              name={
                params.data.scanned_by.first_name +
                " " +
                params.data.scanned_by.last_name
              }
            />
          );
        }
      },
    },
    {
      field: "batch_number",
      headerName: "Barcode",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
  ];

  const formatValueForAgGrid = () => {
    if (traceEntry.length !== 0) {
      const formatValue = traceEntry.map((te, index) => {
        let remark = "";
        let type = "";
        if (te.stock_entry.inventory_object_item) {
          const notNull = te.stock_entry.inventory_object_item;
          if (notNull.goods_receive) {
            remark = notNull.goods_receive.remark;
            type = notNull.goods_receive.type;
          } else if (notNull.goods_issue) {
            remark = notNull.goods_issue.remark;
            type = notNull.goods_issue.type;
          } else if (notNull.goods_transfer)
            remark = notNull.goods_transfer.remark;
          else if (notNull.goods_adjustment)
            remark = notNull.goods_adjustment.remark;
        }
        return {
          ...te,
          source_warehouse_document_id:
            te.stock_entry?.source_warehouse?.document_id,
          source_warehouse_name: te.stock_entry?.source_warehouse?.thai_name,
          source_bin_location_document_id: te.source_bin_location_document_id,
          source_bin_location_name: te.source_bin_location?.name,
          destination_warehouse_document_id:
            te.stock_entry?.destination_warehouse?.document_id,
          destination_warehouse_name:
            te.stock_entry?.destination_warehouse?.thai_name,
          destination_bin_location_document_id:
            te.destination_bin_location_document_id,
          destination_bin_location_name: te.destination_bin_location?.name,
          item_document_id: te.item_document_id,
          item_name: te.item?.name,
          item_description: te.item?.description,
          item_item_group: te.item?.item_group_sub_level_1?.name,
          lot: te.stock_entry.inventory_object_item?.goods_receive?.lot
            ? formatDate(
                te.stock_entry.inventory_object_item?.goods_receive?.lot
              )
            : "",
          remark: remark,
          uom_name: te.uom?.name,
          reference_document_type: entryTypeToThai(
            te.stock_entry?.reference_document_type
          ),
          document_type: type,
          reference_document_id: te.stock_entry?.reference_document_id,
          created_by_full_name: te.created_by
            ? te.created_by?.first_name + " " + te.created_by?.last_name
            : "",
          scanned_by_full_name: te.scanned_by
            ? te.scanned_by?.first_name + " " + te.scanned_by?.last_name
            : "",
          scanned_date: !te.scanned_date
            ? ""
            : unixToDateTimeWithFormat(te.scanned_date),
        };
      });
      return formatValue;
    } else return [];
  };

  const { control, getValues, reset, setValue } = useForm({
    defaultValues: {
      dateType: "yesterday",
      date: moment(new Date()).startOf("day").subtract(1, "day"),
      dateTo: new Date(),
    },
  });

  const exportHandler = async () => {
    setIsLoadingExport(true);
    try {
      let startDate = getValues("date");
      let endDate = getValues("dateTo");

      const params = exportCSVParams(
        t("inventory.traceEntry.index"),
        startDate,
        endDate
      );
      gridRef.current.api.exportDataAsCsv(params);
      enqueueSnackbar("นำออกรายงานสำเร็จ", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("นำออกรายงานไม่สำเร็จ", {
        variant: "error",
      });
    } finally {
      setIsLoadingExport(false);
    }
  };

  const getAllItems = useCallback(async () => {
    let input = {
      startRow: 0,
      endRow: 999999,
      filterModel: {
        created_date: {
          filter: dateToUnix(
            moment(new Date()).startOf("day").subtract(6, "day")
          ),
          filterTo: dateToUnix(moment(new Date()).endOf("day")),
          filterType: "number",
          type: "inRange",
        },
        is_active: {
          filterType: "boolean",
          type: "equals",
          filter: "true",
        },
        is_scanned: {
          filterType: "boolean",
          type: "equals",
          filter: "true",
        },
      },
      sortModel: [],
    };
    const getDateValue = getValues("date");
    const getDateToValue = getValues("dateTo");
    input.filterModel.created_date.filter = dateToUnix(
      moment(getDateValue).startOf("day")
    );
    input.filterModel.created_date.filterTo = dateToUnix(getDateToValue);
    dispatch(getAllTraceEntryReport(input, null, enqueueSnackbar));
  }, [dispatch, enqueueSnackbar, getValues]);

  useEffect(() => {
    if (traceEntry && traceEntry.length > 0)
      setSum(traceEntry.reduce((prev, curr) => prev + curr.posted_quantity, 0));
  }, [traceEntry]);

  const onFilterChanged = () => {
    let newValue = 0;
    gridRef.current.api.forEachNodeAfterFilter(
      (data) => (newValue += data.data.posted_quantity)
    );
    setSum(newValue);
  };

  const onFilterReset = () => {
    if (gridRef) {
      gridRef.current.api.setFilterModel({});
    }
    getAllItems();
    reset();
  };

  useEffect(() => {
    return () => dispatch(inventoryReportActions.resetTraceEntry());
  }, [dispatch]);

  const sideBar = {
    toolPanels: [
      {
        id: "id",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressPivotMode: true,
        },
      },
      "filters",
    ],
  };

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Grid container sx={{ mt: 1 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TotalBox title="ยอดรวม" total={formatNumber(sum ?? 0)} />
        </Grid>
      </Grid>
      <Box sx={{ my: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5"> {t("inventory.traceEntry.index")}</Typography>
        <CustomizedButton
          sx={{ mr: 2 }}
          title={t("inventory.exportReport")}
          variant="contained"
          onClick={exportHandler}
          disabled={isLoadingExport}
        />
      </Box>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={2}>
            <NewReportDateFilter
              t={t}
              control={control}
              setValue={setValue}
              getValues={getValues}
              trace
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.5} alignSelf="center">
            <Box display="flex" gap={2}>
              <CustomizedButton
                title={t("button.submitFilter")}
                variant="contained"
                onClick={() => getAllItems()}
                disabled={isLoading.traceEntry}
                fullWidth
              />
              <CustomizedButton
                title={t("button.resetFilter")}
                variant="outlined"
                onClick={onFilterReset}
                disabled={isLoading.traceEntry}
                fullWidth
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      {traceEntry && traceEntry.length > 0 && (
        <AgGrid
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={formatValueForAgGrid()}
          onFilterChanged={onFilterChanged}
          sideBar={sideBar}
          height={649}
        />
      )}
    </>
  );
};

export default TraceEntry;
